import type { Stripe } from "@stripe/stripe-js";

import { getSubscription } from "@/lib/api/generated";

export type SubscribeOutcome = {
  subscription_id: string;
  status: string;
  type?: "payment" | "setup";
  client_secret?: string;
};

/**
 * Result of confirming a subscription:
 * - `active`: the purchased plan was observed live (`active`/`trialing`).
 * - `pending`: the Stripe intent was confirmed settled, but the plan is not
 *   yet observably live because the finalizing webhook has not landed within
 *   the poll window. The caller must NOT show success: the org may still be on
 *   an incomplete subscription with no credits.
 * - `error`: confirmation failed, details were missing, or no intent was
 *   confirmed and the plan never became observable.
 */
export type ConfirmActivationResult =
  | { readonly status: "active" }
  | { readonly status: "pending" }
  | { readonly status: "error"; readonly error: string };

const INTENT_NOT_SETTLED_ERROR =
  "Payment could not be completed. Please try a different payment method.";

type PollOptions = {
  pollAttempts?: number;
  pollDelayMs?: number;
};

function isSettledIntentStatus(status: string | undefined): boolean {
  return status === "succeeded" || status === "processing";
}

/**
 * Poll `/api/billing/subscription` until the purchased plan is live. Matches
 * `planSlug` so a stale `free`/`active` row left by `execute_cancellation` is
 * not mistaken for the new subscription. Returns true once the purchased plan
 * is `active`/`trialing`, false if the attempts are exhausted first.
 *
 * Safe to call repeatedly: it only reads state, so re-checking after a
 * `pending` result never re-confirms the intent or re-charges the customer.
 */
export async function pollSubscriptionActive(
  planSlug: string,
  opts: PollOptions = {},
): Promise<boolean> {
  const { pollAttempts = 15, pollDelayMs = 1000 } = opts;

  for (let attempt = 0; attempt < pollAttempts; attempt++) {
    const sub = await getSubscription().catch(() => null);
    if (
      sub &&
      sub.plan?.slug === planSlug &&
      (sub.status === "active" || sub.status === "trialing")
    ) {
      return true;
    }
    if (attempt < pollAttempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, pollDelayMs));
    }
  }
  return false;
}

/**
 * Confirm the Stripe intent from `/api/billing/subscribe`, then poll until
 * the purchased plan has gone live. The subscription is created
 * `incomplete`, so the charge only settles once the client confirms here.
 * Entitlements land when the Stripe webhook finalizes the local row.
 *
 * Uses `confirmCardPayment`/`confirmCardSetup` (not the PaymentElement-based
 * `confirmPayment`/`confirmSetup`) because the modal pre-attaches the payment
 * method to the intent.
 *
 * A poll timeout after a settled intent yields `pending`, not success: the
 * payment settled but the webhook has not finalized the plan yet. If no intent
 * was confirmed, a timeout means the subscription never became observable and
 * yields `error`.
 */
export async function confirmAndAwaitActive(
  stripe: Stripe | null,
  res: Pick<SubscribeOutcome, "type" | "client_secret">,
  pmId: string | null,
  planSlug: string,
  opts: PollOptions = {},
): Promise<ConfirmActivationResult> {
  let confirmedIntent = false;

  if (Boolean(res.type) !== Boolean(res.client_secret)) {
    return {
      status: "error",
      error:
        "Subscription response was missing payment confirmation details. Please try again.",
    };
  }

  if (res.type && res.client_secret) {
    if (!stripe) {
      return { status: "error", error: "Payment form not loaded." };
    }
    const data = pmId ? { payment_method: pmId } : undefined;
    let intentStatus: string | undefined;

    if (res.type === "setup") {
      const result = await stripe.confirmCardSetup(res.client_secret, data);
      if (result.error) {
        return {
          status: "error",
          error: result.error.message ?? "Payment confirmation failed.",
        };
      }
      intentStatus = result.setupIntent?.status;
    } else {
      const result = await stripe.confirmCardPayment(res.client_secret, data);
      if (result.error) {
        return {
          status: "error",
          error: result.error.message ?? "Payment confirmation failed.",
        };
      }
      intentStatus = result.paymentIntent?.status;
    }

    // Covers both PaymentIntent and SetupIntent; `processing` is rare for setup.
    if (!isSettledIntentStatus(intentStatus)) {
      return {
        status: "error",
        error: INTENT_NOT_SETTLED_ERROR,
      };
    }
    confirmedIntent = true;
  }

  if (
    await pollSubscriptionActive(planSlug, {
      pollAttempts: opts.pollAttempts,
      pollDelayMs: opts.pollDelayMs,
    })
  ) {
    return { status: "active" };
  }

  if (confirmedIntent) {
    return { status: "pending" };
  }

  return {
    status: "error",
    error: "Subscription confirmation did not complete. Please try again.",
  };
}
